// ============================================================================
// MY TASKS — All tasks assigned to current user (owner / team / cleaner).
// No "view as cleaner" filter: show if assignee_user_id = me (owner/team) or cleaner_id = my cleaner (cleaner).
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { format, addDays } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { getTaskIdsWithChecklistAssignmentRows } from "@/lib/cleaningTaskChecklistAssignments";
import { canStartTurnoverClean, resolveTurnoverWindow, formatTurnoverOpensHint } from "@/lib/turnoverWindow";
import { formatShortAddress } from "@/lib/shortAddress";

export interface MyTaskItem {
  id: string;
  property_id: string;
  scheduled_date: string;
  status: string;
  property_name: string | null;
  checklist_name: string | null;
  /** So UI can route to /clean/ (cleaner) or /tasks/run/ (owner/team). */
  assigned_type: "owner" | "team" | "cleaner" | null;
  guest_name?: string | null;
  reservation_id?: string | null;
  /** Slice 12 turnover window display */
  window_label?: string;
  window_hint?: string;
  window_chip?: string;
  window_times?: string | null;
  /** Slice 12c muted second clock line */
  window_times_secondary?: string | null;
  window_times_tone?: "default" | "teal" | "amber" | "muted";
  guest_left?: boolean;
  waiting_on_guest?: boolean;
  same_day?: boolean;
  can_start?: boolean;
  is_test?: boolean;
  /** Slice 13 identification */
  property_photo_url?: string | null;
  property_address_line?: string | null;
  property_address_sub?: string | null;
  property_street_number?: string | null;
}

export function useMyTasks(dateFilter: "today" | "week" | "all" = "today", propertyId?: string) {
  const { user } = useAuth();
  const { data: cleaner } = useCleanerRecord();

  return useQuery({
    queryKey: ["my-tasks", user?.id, cleaner?.id, dateFilter, propertyId ?? null],
    // Assignments change from another account, so never trust a cached list.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    queryFn: async (): Promise<MyTaskItem[]> => {
      if (!user?.id) return [];

      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() + 14);
      const weekEndStr = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, "0")}-${String(weekEnd.getDate()).padStart(2, "0")}`;
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - 21);
      const weekStartStr = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;

      const rows: Array<{
        id: string;
        property_id: string;
        scheduled_date: string;
        status: string;
        checklist_id: string | null;
        assigned_type: string | null;
        reservation_id: string | null;
        is_test: boolean;
      }> = [];
      const seen = new Set<string>();

      const pushRow = (r: {
        id: string;
        property_id: string;
        scheduled_date: string;
        status: string;
        checklist_id?: string | null;
        assigned_type?: string | null;
        reservation_id?: string | null;
        is_test?: boolean | null;
      }, assignedTypeOverride?: string | null) => {
        if (seen.has(r.id)) return;
        seen.add(r.id);
        rows.push({
          id: r.id,
          property_id: r.property_id,
          scheduled_date: r.scheduled_date,
          status: r.status,
          checklist_id: r.checklist_id ?? null,
          assigned_type: assignedTypeOverride ?? r.assigned_type ?? null,
          reservation_id: r.reservation_id ?? null,
          is_test: Boolean(r.is_test),
        });
      };

      const applyDateFilter = <T extends { gte: Function; lte: Function; eq: Function }>(q: T): T => {
        if (dateFilter === "today") {
          // Today + overdue open work in the recent window (not only exact calendar day).
          return q.gte("scheduled_date", weekStartStr).lte("scheduled_date", today) as T;
        }
        if (dateFilter === "week") {
          return q.gte("scheduled_date", weekStartStr).lte("scheduled_date", weekEndStr) as T;
        }
        return q;
      };

      // 1a) Owner/team on main task row
      let ownerTeamQuery = supabase
        .from("cleaning_tasks")
        .select("id, property_id, scheduled_date, status, checklist_id, assigned_type, reservation_id, is_test")
        .in("status", ["scheduled", "in_progress"])
        .in("assigned_type", ["owner", "team"])
        .eq("assignee_user_id", user.id);
      if (propertyId) ownerTeamQuery = ownerTeamQuery.eq("property_id", propertyId);
      ownerTeamQuery = applyDateFilter(ownerTeamQuery);
      ownerTeamQuery = ownerTeamQuery.order("scheduled_date", { ascending: true }).order("created_at", { ascending: true });

      const { data: ownerTeamData, error: err1 } = await ownerTeamQuery;
      if (!err1 && ownerTeamData) {
        for (const r of ownerTeamData) pushRow(r);
      }

      // 1b) Owner/team via checklist assignment rows (covers legacy saves that cleared main assignee)
      const { data: assignmentRows } = await supabase
        .from("cleaning_task_checklist_assignments")
        .select("cleaning_task_id, assigned_type")
        .eq("assignee_user_id", user.id)
        .in("assigned_type", ["owner", "team"]);
      const viaAssignmentIds = [
        ...new Set((assignmentRows ?? []).map((r) => r.cleaning_task_id).filter(Boolean)),
      ] as string[];
      const assignedTypeByTaskId = new Map<string, string>();
      for (const r of assignmentRows ?? []) {
        if (r.cleaning_task_id && r.assigned_type && !assignedTypeByTaskId.has(r.cleaning_task_id)) {
          assignedTypeByTaskId.set(r.cleaning_task_id, r.assigned_type);
        }
      }
      if (viaAssignmentIds.length > 0) {
        let viaQuery = supabase
          .from("cleaning_tasks")
          .select("id, property_id, scheduled_date, status, checklist_id, assigned_type, reservation_id, is_test")
          .in("id", viaAssignmentIds)
          .in("status", ["scheduled", "in_progress"]);
        if (propertyId) viaQuery = viaQuery.eq("property_id", propertyId);
        viaQuery = applyDateFilter(viaQuery);
        viaQuery = viaQuery.order("scheduled_date", { ascending: true }).order("created_at", { ascending: true });
        const { data: viaData, error: viaErr } = await viaQuery;
        if (!viaErr && viaData) {
          for (const r of viaData) {
            pushRow(r, assignedTypeByTaskId.get(r.id) ?? r.assigned_type);
          }
        }
      }

      // 2) Cleaner: (a) main task row cleaner_id = my cleaner; (b) or assigned via cleaning_task_checklist_assignments
      if (cleaner?.id) {
        // 2a) Tasks with cleaner_id set on cleaning_tasks (single-assignee flow).
        let cleanerQuery = supabase
          .from("cleaning_tasks")
          .select("id, property_id, scheduled_date, status, checklist_id, assigned_type, reservation_id, is_test")
          .eq("cleaner_id", cleaner.id)
          .eq("assigned_type", "cleaner")
          .in("status", ["scheduled", "in_progress"]);
        if (propertyId) cleanerQuery = cleanerQuery.eq("property_id", propertyId);
        cleanerQuery = applyDateFilter(cleanerQuery);
        cleanerQuery = cleanerQuery.order("scheduled_date", { ascending: true }).order("created_at", { ascending: true });

        const { data: cleanerData, error: err2 } = await cleanerQuery;
        if (!err2 && cleanerData && cleanerData.length > 0) {
          const withJunction = await getTaskIdsWithChecklistAssignmentRows(cleanerData.map((r) => r.id));
          for (const r of cleanerData) {
            if (withJunction.has(r.id)) continue;
            pushRow(r, "cleaner");
          }
        }

        // 2b) Tasks where cleaner is assigned only in cleaning_task_checklist_assignments
        const { data: viaAssignments } = await supabase
          .from("cleaning_task_checklist_assignments")
          .select("cleaning_task_id")
          .eq("cleaner_id", cleaner.id);
        const viaCleanerAssignmentIds = [...new Set((viaAssignments ?? []).map((r) => r.cleaning_task_id))];
        if (viaCleanerAssignmentIds.length > 0) {
          let viaQuery = supabase
            .from("cleaning_tasks")
            .select("id, property_id, scheduled_date, status, checklist_id, assigned_type, reservation_id, is_test")
            .in("id", viaCleanerAssignmentIds)
            .in("status", ["scheduled", "in_progress"]);
          if (propertyId) viaQuery = viaQuery.eq("property_id", propertyId);
          viaQuery = applyDateFilter(viaQuery);
          viaQuery = viaQuery.order("scheduled_date", { ascending: true }).order("created_at", { ascending: true });
          const { data: viaData, error: err3 } = await viaQuery;
          if (!err3 && viaData) {
            for (const r of viaData) pushRow(r, "cleaner");
          }
        }
      }

      rows.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || a.id.localeCompare(b.id));

      const propertyIds = [...new Set(rows.map((r) => r.property_id))];
      const checklistIds = [...new Set(rows.map((r) => r.checklist_id).filter(Boolean))] as string[];
      const reservationIds = [...new Set(rows.map((r) => r.reservation_id).filter(Boolean))] as string[];
      const propertyMap = new Map<
        string,
        {
          name: string;
          photo_url: string | null;
          address: string | null;
          formatted_address: string | null;
          unit: string | null;
          unit_location: string | null;
        }
      >();
      const checklistMap = new Map<string, string>();
      const guestNameByResId = new Map<string, string>();
      if (propertyIds.length > 0) {
        const { data: props } = await supabase
          .from("properties")
          .select("id, name, address, formatted_address, unit, unit_location, property_photo_url, property_photo_urls")
          .in("id", propertyIds);
        for (const p of props ?? []) {
          const row = p as {
            id: string;
            name?: string | null;
            address?: string | null;
            formatted_address?: string | null;
            unit?: string | null;
            unit_location?: string | null;
            property_photo_url?: string | null;
            property_photo_urls?: string[] | null;
          };
          const photo =
            row.property_photo_url?.trim() ||
            row.property_photo_urls?.[0]?.trim() ||
            null;
          propertyMap.set(row.id, {
            name: row.name?.trim() || "",
            photo_url: photo,
            address: row.address?.trim() || null,
            formatted_address: row.formatted_address?.trim() || null,
            unit: row.unit?.trim() || null,
            unit_location: row.unit_location?.trim() || null,
          });
        }
      }
      if (checklistIds.length > 0) {
        const { data: cls } = await supabase.from("checklists").select("id, name").in("id", checklistIds);
        (cls ?? []).forEach((c: { id: string; name?: string }) => checklistMap.set(c.id, c.name ?? ""));
      }
      if (reservationIds.length > 0) {
        const { data: res } = await supabase.from("reservations").select("id, guest_name").in("id", reservationIds);
        (res ?? []).forEach((r: { id: string; guest_name?: string | null }) => guestNameByResId.set(r.id, r.guest_name ?? ""));
      }

      // Reservations for window labels (checkout → next check-in) per property
      const reservationsByProperty = new Map<string, Array<{ id: string; start_date: string; end_date: string; guest_name?: string | null; guest_count?: number | null; check_in_time?: string | null; check_out_time?: string | null; check_out_previous?: string | null; check_out_slipped_at?: string | null; early_checkout_detected?: boolean | null; checkout_unconfirmed_at?: string | null; reservation_type?: string | null }>>();
      if (propertyIds.length > 0) {
        const past = new Date();
        past.setDate(past.getDate() - 90);
        const pastStr = `${past.getFullYear()}-${String(past.getMonth() + 1).padStart(2, "0")}-${String(past.getDate()).padStart(2, "0")}`;
        const { data: allRes } = await supabase
          .from("reservations")
          .select("id, property_id, start_date, end_date, guest_name, guest_count, check_in_time, check_out_time, check_out_previous, check_out_slipped_at, reservation_type, early_checkout_detected, checkout_unconfirmed_at")
          .in("property_id", propertyIds)
          .gte("start_date", pastStr);
        for (const r of allRes ?? []) {
          const pid = (r as { property_id: string }).property_id;
          const list = reservationsByProperty.get(pid) ?? [];
          list.push(r as { id: string; start_date: string; end_date: string; guest_name?: string | null; guest_count?: number | null; check_in_time?: string | null; check_out_time?: string | null; check_out_previous?: string | null; check_out_slipped_at?: string | null; reservation_type?: string | null; early_checkout_detected?: boolean | null; checkout_unconfirmed_at?: string | null });
          reservationsByProperty.set(pid, list);
        }
      }

      return rows.map((r) => {
        const resolved = resolveTurnoverWindow({
          checkoutDate: r.scheduled_date,
          reservations: reservationsByProperty.get(r.property_id) ?? [],
          departingReservationId: r.reservation_id,
          taskStatus: r.status,
          isTest: r.is_test,
        });
        const canStart = r.status === "in_progress" || canStartTurnoverClean(r.scheduled_date);
        const hint = !canStart
          ? formatTurnoverOpensHint(r.scheduled_date) || "Not open yet"
          : null;
        const prop = propertyMap.get(r.property_id);
        const propertyName = prop?.name || null;
        const addr = formatShortAddress({
          address: prop?.address,
          formattedAddress: prop?.formatted_address,
          unit: prop?.unit,
          unitLocation: prop?.unit_location,
          propertyName,
          windowLabel: resolved.label,
        });
        return {
          id: r.id,
          property_id: r.property_id,
          scheduled_date: r.scheduled_date,
          status: r.status,
          property_name: propertyName,
          checklist_name: (r.checklist_id && checklistMap.get(r.checklist_id)) ?? null,
          assigned_type: (r.assigned_type as "owner" | "team" | "cleaner") || null,
          guest_name: (r.reservation_id && guestNameByResId.get(r.reservation_id)) ?? null,
          reservation_id: r.reservation_id,
          window_label: resolved.label,
          window_hint: hint,
          window_chip: resolved.chip,
          window_times: resolved.cleanerClock,
          window_times_secondary: resolved.cleanerClockSecondary,
          window_times_tone: resolved.cleanerClockTone,
          guest_left: resolved.guestLeft,
          waiting_on_guest: resolved.waitingOnGuest,
          checkout_updated: resolved.checkoutUpdated,
          status_note: resolved.statusNote,
          same_day: resolved.sameDay,
          can_start: canStart,
          is_test: r.is_test,
          property_photo_url: prop?.photo_url ?? null,
          property_address_line: addr.line1,
          property_address_sub: addr.line2,
          property_street_number: addr.streetNumber,
        };
      });
    },
    enabled: !!user?.id,
  });
}

export interface MyPreventativeTaskItem {
  id: string;
  property_id: string;
  scheduled_date: string;
  notes: string | null;
  checklist_id: string | null;
  task_type: string | null;
  preventative_maintenance_id: string | null;
  status: string;
  properties: { name?: string } | null;
}

export function useMyPreventativeTasks() {
  const { user } = useAuth();
  const { data: cleaner } = useCleanerRecord();

  return useQuery({
    queryKey: ["my-preventative-tasks", user?.id, cleaner?.id],
    queryFn: async (): Promise<MyPreventativeTaskItem[]> => {
      if (!user?.id) return [];

      const today = format(new Date(), "yyyy-MM-dd");
      const twoWeeks = format(addDays(new Date(), 14), "yyyy-MM-dd");

      const select =
        "id, property_id, scheduled_date, notes, checklist_id, task_type, preventative_maintenance_id, status, properties(name)";

      const queries: ReturnType<typeof supabase.from>[] = [];

      if (cleaner?.id) {
        queries.push(
          supabase
            .from("cleaning_tasks")
            .select(select)
            .eq("task_type", "preventative_maintenance")
            .gte("scheduled_date", today)
            .lte("scheduled_date", twoWeeks)
            .in("status", ["scheduled", "in_progress"])
            .eq("cleaner_id", cleaner.id)
        );
      }

      queries.push(
        supabase
          .from("cleaning_tasks")
          .select(select)
          .eq("task_type", "preventative_maintenance")
          .gte("scheduled_date", today)
          .lte("scheduled_date", twoWeeks)
          .in("status", ["scheduled", "in_progress"])
          .eq("assignee_user_id", user.id)
      );

      const results = await Promise.all(queries);
      const combined = results.flatMap((r) => r.data ?? []) as MyPreventativeTaskItem[];

      const seen = new Set<string>();
      return combined.filter((t) => {
        if (seen.has(t.id)) return false;
        seen.add(t.id);
        return true;
      });
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });
}
